/**
 * Composant Combobox
 * Input avec recherche et suggestions dropdown
 * Permet de taper librement OU sélectionner dans une liste
 */

import { useState, useRef, useEffect, forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { Search, X, Check } from 'lucide-react';

export interface ComboboxOption {
    id: string;
    label: string;
    sublabel?: string;
}

export interface ComboboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'onSelect'> {
    options: ComboboxOption[];
    value: string;
    selectedId?: string;
    onChange: (value: string, option?: ComboboxOption) => void;
    onSelect?: (option: ComboboxOption) => void;
    onClear?: () => void;
    error?: boolean;
    emptyMessage?: string;
}

const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(
    ({ options, value, selectedId, onChange, onSelect, onClear, error, emptyMessage = 'Aucun résultat', className, placeholder, ...props }, ref) => {
        const [isOpen, setIsOpen] = useState(false);
        const [focused, setFocused] = useState(false);
        const containerRef = useRef<HTMLDivElement>(null);

        // Filter options based on input value
        const filtered = value
            ? options.filter(opt =>
                opt.label.toLowerCase().includes(value.toLowerCase()) ||
                (opt.sublabel && opt.sublabel.toLowerCase().includes(value.toLowerCase()))
            )
            : options;

        // Close on outside click
        useEffect(() => {
            const handleClickOutside = (e: MouseEvent) => {
                if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);

        const handleSelect = (option: ComboboxOption) => {
            onChange(option.label, option);
            onSelect?.(option);
            setIsOpen(false);
        };

        const handleClear = () => {
            onChange('');
            onClear?.();
        };

        return (
            <div ref={containerRef} className="relative">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8] pointer-events-none" />
                    <input
                        ref={ref}
                        type="text"
                        value={value}
                        onChange={(e) => {
                            onChange(e.target.value);
                            setIsOpen(true);
                        }}
                        onFocus={() => {
                            setFocused(true);
                            setIsOpen(true);
                        }}
                        onBlur={() => setFocused(false)}
                        placeholder={placeholder || 'Rechercher ou saisir...'}
                        className={cn(
                            'flex h-10 w-full rounded-lg border bg-white dark:bg-[#0F172A] dark:text-[#F1F5F9] pl-9 pr-9 py-2 text-sm',
                            'placeholder:text-[#94A3B8] dark:placeholder:text-[#64748B]',
                            'focus:outline-none focus:ring-2 focus:ring-offset-0',
                            'transition-all duration-200',
                            error
                                ? 'border-[#EF4444] focus:ring-[#EF4444]/20'
                                : 'border-[#E2E8F0] dark:border-[#334155] focus:ring-[#0F2744]/20 focus:border-[#0F2744] dark:focus:border-[#3B82F6]',
                            selectedId && 'border-[#10B981] bg-[#F0FDF4]/50 dark:bg-[#10B981]/10',
                            className
                        )}
                        {...props}
                    />
                    {value && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#64748B] dark:hover:text-[#F1F5F9] transition-colors"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>

                {/* Dropdown */}
                {isOpen && (focused || isOpen) && (
                    <div className="absolute z-50 mt-1 w-full rounded-lg border border-[#E2E8F0] dark:border-[#334155] bg-white dark:bg-[#1E293B] shadow-lg animate-in fade-in-0 zoom-in-95 max-h-60 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-6 text-center text-sm text-[#94A3B8] dark:text-[#64748B]">
                                {value ? emptyMessage : 'Commencez à taper...'}
                            </div>
                        ) : (
                            filtered.map(option => (
                                <button
                                    key={option.id}
                                    type="button"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleSelect(option)}
                                    className={cn(
                                        'flex items-center justify-between w-full px-3 py-2.5 text-left text-sm',
                                        'hover:bg-[#F1F5F9] dark:hover:bg-[#334155] transition-colors duration-150',
                                        'first:rounded-t-lg last:rounded-b-lg',
                                        selectedId === option.id && 'bg-[#EFF6FF] dark:bg-[#0F2744]/20'
                                    )}
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="font-medium text-[#1E293B] dark:text-[#F1F5F9] truncate">{option.label}</p>
                                        {option.sublabel && (
                                            <p className="text-xs text-[#64748B] dark:text-[#94A3B8] truncate">{option.sublabel}</p>
                                        )}
                                    </div>
                                    {selectedId === option.id && (
                                        <Check className="h-4 w-4 text-[#1D4ED8] dark:text-[#60A5FA] flex-shrink-0 ml-2" />
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
        );
    }
);

Combobox.displayName = 'Combobox';

export { Combobox };
